// SOR-625: the mobile Filter & sort bottom sheet — Park + Sort move here
// from the header row below sm (the header keeps its pills at sm: and up,
// so the whole component is sm:hidden).
//
// Draft-commit model: pill taps edit a LOCAL draft; nothing filters until
// the single 48px primary button ("Show N streams") commits it. Tapping
// the scrim or pressing Escape discards the draft.
//
// Stacking: the tab bar is z-30; the scrim is z-40 (covering — and so
// dimming — the tab bar) and the sheet is z-50. BrowseScreen marks the tab
// bar AND the header/grid inert while the sheet is open, so aria-modal
// matches reality; this component owns moving focus into the dialog on
// open and restoring it to the opener on close.
//
// Bootstrap-collision rule: base classes whose names Bootstrap also defines
// (px-4, pt-3, pt-4, rounded-pill, …) are written as arbitrary values
// (px-[16px]) — Bootstrap's !important utilities otherwise override the
// browse tokens (e.g. rounded-pill would render 50rem, not the 9px token).
//
// All colors come from the browse-* tokens (raw hex fails the designTokens
// jest check).
import React, { useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';

// before: pseudo pads the 40px pill out to a 44px touch target
const PILL_BASE =
  'relative before:absolute before:inset-x-0 before:top-[-2px] before:bottom-[-2px] flex h-10 items-center whitespace-nowrap rounded-[9px] border px-3.5 font-inter-tight text-13 font-medium leading-none';
const PILL_IDLE = 'border-browse-border-strong text-browse-text-control';
const PILL_ON = 'border-browse-accent text-browse-accent';

const GroupLabel = ({ children }) => (
  <h3 className="pb-2 pt-[16px] font-inter-tight text-13 font-medium text-browse-text-tertiary">
    {children}
  </h3>
);

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose  discards the draft
 * @param {string[]} props.parks  every park/resort tag in the fetched streams
 * @param {string} props.park  the committed park filter ('all' = no filter)
 * @param {(p: string) => void} props.onParkChange  commit target
 * @param {(p: string) => number} props.countFor  how many streams the grid
 *   would show if the draft park were committed — powers "Show N streams"
 */
const FilterSheet = ({ open, onClose, parks, park, onParkChange, sort = 'watched', onSortChange = () => {}, showRising = false, countFor }) => {
  const [draft, setDraft] = useState(park);
  const [draftSort, setDraftSort] = useState(sort);
  const dialogRef = useRef(null);

  // re-seed the draft from the committed value each time the sheet opens
  useEffect(() => {
    if (open) {
      setDraft(park);
      setDraftSort(sort);
    }
  }, [open, park, sort]);

  // modal plumbing: focus the dialog on open and give focus back to the
  // opener (the Filter chip) on close; lock background scroll meanwhile
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    dialogRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const n = countFor(draft);

  const commit = () => {
    onParkChange(draft);
    onSortChange(draftSort);
    posthog.capture('browse_park_filtered', { park: draft });
    onClose();
  };

  return (
    <div className="sm:hidden">
      {/* scrim covers the WHOLE viewport — tab bar included, which is how
          the bar dims — and a tap on it discards the draft */}
      <div
        aria-hidden="true"
        data-testid="filter-sheet-scrim"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-browse-scrim-soft"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Filter & sort"
        ref={dialogRef}
        tabIndex={-1}
        className="fixed inset-x-0 bottom-0 z-50 rounded-t-[20px] bg-browse-surface px-[16px] pt-2.5 pb-[calc(16px_+_env(safe-area-inset-bottom))] focus:outline-none"
      >
        {/* grab handle */}
        <span
          aria-hidden="true"
          className="mx-auto block h-1 w-9 rounded-[9px] bg-browse-placeholder-grey"
        />
        <div className="flex items-center justify-between pt-[12px]">
          <h2 className="font-inter-tight text-base font-semibold leading-1.2 text-browse-text-primary">
            Filter &amp; sort
          </h2>
          <button
            type="button"
            onClick={() => setDraft('all')}
            className="flex h-11 items-center px-2 font-inter-tight text-13 font-medium text-browse-accent"
          >
            Reset
          </button>
        </div>
        {/* the option groups scroll so a long park list can't push the
            handle/title off-screen; the 48px primary stays pinned below */}
        <div className="max-h-[55vh] overflow-y-auto">
          <GroupLabel>Park</GroupLabel>
          <div className="flex flex-wrap gap-1.75">
            {['all', ...parks].map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={draft === p}
                onClick={() => setDraft(p)}
                className={`${PILL_BASE} ${draft === p ? PILL_ON : PILL_IDLE}`}
              >
                {p === 'all' ? 'All parks' : p}
              </button>
            ))}
          </div>
          <GroupLabel>Sort</GroupLabel>
          {[['watched', 'Most watched'], ...(showRising ? [['rising', 'Rising']] : [])].map(
            ([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={draftSort === key}
                onClick={() => setDraftSort(key)}
                className={`${PILL_BASE} ${draftSort === key ? PILL_ON : PILL_IDLE}`}
              >
                {label}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          onClick={commit}
          className="mt-4.5 flex h-12 w-full items-center justify-center rounded-[9px] bg-browse-accent font-inter-tight text-sm font-semibold text-white"
        >
          Show {n} stream{n === 1 ? '' : 's'}
        </button>
      </div>
    </div>
  );
};

export default FilterSheet;
